/**
* Licensed Materials - Property of IBM
* IBM Watson Analytics (C) Copyright IBM Corp. 2018
* US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
*
*/
define([
	'require',
	'underscore',
	'./ConverterInterface'
], function(
	require,
	_,
	ConverterInterface
) {
	class ConversionService {
		constructor() {
			// Map of srvc providers
			// Example of registered provider:
			// {
			// 	src: 'DASHBOARD',
			// 	target: 'EXPLORE',
			// 	converter: Class
			// }
			this._providers = [];
			this._glassContext = null;
			this.errLog = [];
		}

		/**
		* @param {object} glassContext - The glassContext when the service is created
		*/
		initialize(glassContext) {
			this._glassContext = glassContext;

			return this._getGlassContextSrvcCollection()
				.then(serviceProviders => Promise.all(serviceProviders.map(provider =>
					this._createServiceInstance(provider.class)
						.then(providerClass => this.registerProvider(providerClass))
						.catch(err => this.errLog.push(err)) // we swallow errors from invalid providers for now
				)));
		}

		/**
		* @return {Class} ConverterInterface to extend from for new converters
		*/
		getInterface() {
			return ConverterInterface;
		}

		/**
		* @param {Class} handler - conversion handler class. Needs a src, target, and functions convert and validate
		*/
		registerProvider(handler) {
			return new Promise((resolve, reject) => {
				let instance = new handler({ glassContext: this._glassContext });
				if (!this._isValidProvider(instance)) return reject({ msg: 'Invalid provider', moduleClass: handler });
				this._providers.push({
					src: instance.src,
					target: instance.target,
					converter: instance
				});
				return resolve();
			});
		}

		/**
		 * @return {Promise} resolve with a list of errors we have from invalid providers
		 */
		getErrors() {
			return Promise.resolve(this.errLog);
		}

		/**
		* @param {String} src - source spec to convert from
		* @param {String} target - target spec to convert to
		* @return {Promise} will resolve with the registered conversion provider or null/undefined
		*/
		getProvider(src, target) {
			return this._getServiceProvider(src, target);
		}

		/**
		* @param {String} src - source spec to convert from
		* @param {String} target - target spec to convert to
		* @param {String} spec -  spec to convert
		* @return {Promise} will find correct provider, convert, and resolve with converted spec
		*/
		convert(src, target, spec) {
			return this.getProvider(src,target)
				.then(provider => provider.converter.convert(src, target, spec));
		}

		/**
		* @private
		*/
		_isValidProvider(handler) {
			return (handler &&
				handler.convert && _.isFunction(handler.convert) &&
				handler.validate && _.isFunction(handler.validate) &&
				!_.isUndefined(handler.src) && !_.isNull(handler.src) &&
				!_.isUndefined(handler.target) && !_.isNull(handler.target));
		}

		/**
		* @private
		*/
		_getServiceProvider(src, target) {
			var provider = this._getProviderLocal(src, target);
			if (provider) {
				return Promise.resolve(provider);
			}

			return this._getConversionServiceProvider(src, target).then(provider => {
				if (!provider) {
					return Promise.reject({ errorKey: 'conversionProviderNotFound' });
				}
				return this._getNewServiceInstance(provider.class, src, target);
			});
		}

		/**
		* @private
		*/
		_getProviderLocal(src, target) {
			return this._find(src, target, this._providers);
		}

		/**
		* @private
		*/
		_getConversionServiceProvider(src, target) {
			return this._getGlassContextSrvcCollection()
				.then(serviceProviders => this._find(src, target, serviceProviders));
		}

		/**
		* @private
		*/
		_find(src, target, providers) {
			return providers.find(provider => provider.src == src && provider.target == target);
		}

		/**
		* @private
		* @return Promise resolves with glass collection
		*/
		_getGlassContextSrvcCollection() {
			return this._glassContext.appController.findCollection('com.ibm.bi.ConversionService.provider');
		}

		/**
		 * @private
		*/
		_createServiceInstance(moduleId) {
			return new Promise((resolve, reject) => {
				if (typeof moduleId == 'function') return resolve(moduleId);
				try {
					require([moduleId], (moduleClass) => {
						return resolve(moduleClass);
					}, (error) => {
						return reject(error);
					});
				} catch (error) {
					return reject({ msg: 'Require failed', err: error, classPath: moduleId });
				}
			});
		}

		/**
		* @private
		* @param {string} moduleId moduleClassPath
		* @param {string} src converter of src to get
		* @param {string} target converter with this this.target
		* @return {Promise} moduleInstance an instance of the converter
		*/
		_getNewServiceInstance(moduleId, src, target) {
			return this._createServiceInstance(moduleId)
				.then(moduleClass => this.registerProvider(moduleClass))
				.then(() => this.getProvider(src, target))
				.catch(err => this.errLog.push(err));
		}
	}

	return ConversionService;
});
